import { Box, BoxProps, Divider, Menu, MenuItem } from '@material-ui/core';
import { useState } from 'react';

import { CustomTimeIntervalPopover } from '@/components/modules/Metrics/MetricAggregateViews/components/CustomTimeIntervalPopover';
import { StyledButton } from '@/components/modules/Metrics/shared/components/StyledButton';
import { useTimeIntervalLabel } from '@/components/modules/Metrics/shared/hooks/useTimeIntervalLabel';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';

import { TimeInterval } from '../../shared/entities/TimeInterval';
import { TimeIntervalType } from '../../shared/types/TimeIntervalType';

const PRESET_TIME_INTERVAL_TYPES = [
  TimeIntervalType.TODAY,
  TimeIntervalType.THIS_WEEK,
  TimeIntervalType.THIS_MONTH,
  TimeIntervalType.MONTH_TO_DATE,
];

interface TimeIntervalSelectorProps extends BoxProps {
  timeInterval: TimeInterval;
  setTimeInterval: React.Dispatch<React.SetStateAction<TimeInterval>>;
  type?: 'button' | 'select';
}

export const TimeIntervalSelector = ({
  timeInterval,
  setTimeInterval,
  type = 'button',
  ...props
}: TimeIntervalSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [customAnchorEl, setCustomAnchorEl] = useState<null | HTMLElement>(null);
  const label = useTimeIntervalLabel(timeInterval);

  if (type === 'select') {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <Box {...props}>
        <StyledButton
          value={timeInterval.type}
          label={label}
          onClick={handleClick}
          timeInterval={timeInterval}
          color="default"
          startIcon={<Icon size={16}>calendar_today</Icon>}
          endIcon={<Icon size={20}>expand_more</Icon>}
        />
        <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
          {PRESET_TIME_INTERVAL_TYPES.map((value) => (
            <MenuItem
              dense
              key={value}
              value={value}
              onClick={() => {
                setTimeInterval(new TimeInterval(value));
                handleClose();
              }}
              selected={value === timeInterval.type}
            >
              <Text tId={value} />
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            dense
            value={TimeIntervalType.CUSTOM}
            onClick={() => {
              setCustomAnchorEl(anchorEl);
              handleClose();
            }}
            selected={timeInterval.type === TimeIntervalType.CUSTOM}
          >
            <Text tId={TimeIntervalType.CUSTOM} />
          </MenuItem>
        </Menu>
        {customAnchorEl && (
          <CustomTimeIntervalPopover
            anchorEl={customAnchorEl}
            timeInterval={timeInterval}
            onClose={() => setCustomAnchorEl(null)}
            setTimeInterval={setTimeInterval}
          />
        )}
      </Box>
    );
  }

  return (
    <Box display={'flex'} flexDirection={'column'} minWidth={'fit-content'} {...props}>
      {PRESET_TIME_INTERVAL_TYPES.map((value) => (
        <StyledButton
          key={value}
          value={value}
          onClick={() => setTimeInterval(new TimeInterval(value))}
          timeInterval={timeInterval}
          style={{ marginBottom: 12 }}
        />
      ))}
    </Box>
  );
};
